import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import type {
  AssociationProductFragmentEnhanced,
  ModelAssociationFragmentEnhanced,
  ProductFragmentEnhanced,
} from '@models/enhanced-fragments';
import {
  isEicShopSubmitPostMessageDataV1,
  isExchangeConfigurationV1,
  isEicAbortPostMessageData,
} from '@igus/echain-shared-exchange';
import type { EicShopSubmitPostMessageDataV1 } from '@igus/echain-shared-exchange';
import { useEchainProductStore } from './states/useEchainProductStore';
import { processIaConfigEvents } from '@libs/ia-config-events';
import { useIAConfigurator } from './useIAConfigurator';
import { useProductPageStore } from './states/useProductPageStore';
import { getArticleDetail, getArticlePrice } from '@libs/catalog-service';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '@libs/constants';
import { getPriceDisplayTypeForOrderType, mapArticleDetailResponseToPrices } from '@libs/product-price-utils';

import type {
  InteriorSeparationConfiguration,
  EchainPartsListItem,
  PricesAndOrderTypeDataMapped,
} from './states/echainPartsListSlice';
import { useSessionStore } from './states/useSessionStore';
import type { AssociatedProductPrice } from '@product/ProductPage/ProductPage';
import { getPriceCountry } from '@libs/get-price-country.helper';

export interface IAConfigEventHandlerParams {
  event: { data: EicShopSubmitPostMessageDataV1 };
  ketteNumGlieder: number;
  interiorSeparatorProducts: AssociationProductFragmentEnhanced[] | null;
  interiorShelveProducts: ModelAssociationFragmentEnhanced[] | undefined;
  locale: string | undefined;
  priceCountry: string;
  setIaConfig: (iaConfig: InteriorSeparationConfiguration, iaConfigParts: EchainPartsListItem[]) => void;
  closeIaConfiguratorWindow: () => void;
  updateIaConfigItemPricesAndOrderType: (pricesAndOrderTypeMap: PricesAndOrderTypeDataMapped) => void;
  updateCablePrices: () => void;
  getCurrentProduct: () => ProductFragmentEnhanced;
  setSelectedVariantSku: (productId: string) => void;
  setHasProductVariantSelectionChanged: (changed: boolean) => void;
}

export async function fetchPricesForUnknownProducts(
  parts: EchainPartsListItem[] | undefined,
  priceCountry: string,
  updateIaConfigItemPricesAndOrderType: (iaItemPrices: PricesAndOrderTypeDataMapped) => void
) {
  /*
  We assume that we do not need to distinguish between the default product id and the US-specific product id here.
  When a customer is on the US shop the configuration should contain US-specific product ids and we only end up here
  for products that are not in our shop database. Thus, there would be no chance to map to the US-specific product id.
   */
  const unknownItemIds = parts?.filter(item => item.unknownProduct === true).map(item => item.id);
  if (unknownItemIds && unknownItemIds.length > 0) {
    const [articlePrices, articleDetails] = await Promise.all([
      getArticlePrice({ articleNumbers: unknownItemIds }),
      getArticleDetail({ articleNumbers: unknownItemIds, textMode: false }),
    ]);

    const articleDataMapped = articleDetails.articles
      .map(articleDetailRecord => {
        const articlePriceRecord = articlePrices.articles.find(
          priceRecord => priceRecord.articleNumber === articleDetailRecord.articleNumber
        );

        if (!articlePriceRecord) {
          throw new Error(`Cannot find /article/price response for ${articleDetailRecord.articleNumber}`);
        }

        if (articlePriceRecord?.orderingType === 0) {
          return undefined;
        }

        const orderType = articlePriceRecord.orderingType;
        const priceType = getPriceDisplayTypeForOrderType(`${orderType}`);

        const [price] = mapArticleDetailResponseToPrices(
          {
            articles: [articleDetailRecord],
            currency: articleDetails.currency,
            decimalPlaces: articleDetails.decimalPlaces,
          },
          priceType,
          priceCountry
        );
        return { price, orderType };
      })
      .filter((price): price is { price: AssociatedProductPrice; orderType: number } => price !== undefined);

    const iaItemPricesAndOrderType: PricesAndOrderTypeDataMapped = {};
    unknownItemIds.forEach(productId => {
      const articleData = articleDataMapped.find(({ price }) => price.id === productId);
      if (!articleData) {
        iaItemPricesAndOrderType[productId] = {
          priceData: { id: productId, price: -1, currency: DEFAULT_CURRENCY },
          orderType: 100,
          prices: [],
        };
        return;
      }
      iaItemPricesAndOrderType[productId] = {
        priceData: {
          id: productId,
          price: articleData?.price.baseUnitCentPrice ? articleData.price.baseUnitCentPrice / 100 : -1,
          currency: articleData?.price.quantityDiscounts[0].currency_code,
        },
        prices: articleData?.price.quantityDiscounts,
        orderType: articleData.orderType,
      };
    });

    updateIaConfigItemPricesAndOrderType(iaItemPricesAndOrderType);
  }
  return Promise.resolve();
}

/**
 * The eventHandler to process events handed over from IA Configurator (Innenaufteilungskonfigurator)
 */
export const iaConfigEventHandler = async (params: IAConfigEventHandlerParams) => {
  if (!params.event?.data) {
    return;
  }

  // ia configurator endpoints are versioned, we currently only use v1, therefore we need to make sure the event and payload is in the correct version
  if (
    isEicShopSubmitPostMessageDataV1(params.event.data) &&
    isExchangeConfigurationV1(params.event.data.payload?.exchangeConfiguration)
  ) {
    const { processedIAConfig: iaConfig, parts } = await processIaConfigEvents(
      params.event.data,
      params.ketteNumGlieder,
      params.interiorSeparatorProducts,
      params.interiorShelveProducts,
      params.locale || DEFAULT_LOCALE,
      params.priceCountry
    );
    params.closeIaConfiguratorWindow();

    if (iaConfig?.productId && params.getCurrentProduct().id !== iaConfig?.productId) {
      params.setSelectedVariantSku(iaConfig?.productId);
      params.setHasProductVariantSelectionChanged(true);
    }

    if (iaConfig) {
      params.setIaConfig(iaConfig, parts);

      await fetchPricesForUnknownProducts(parts, params.priceCountry, params.updateIaConfigItemPricesAndOrderType);
      params.updateCablePrices();
    }
  } else if (isEicAbortPostMessageData(params.event.data)) {
    params.closeIaConfiguratorWindow();
  }
};

export const useIAConfiguratorEvents = (
  chainId: string,
  chainLength: number,
  interiorSeparatorProducts: AssociationProductFragmentEnhanced[] | null,
  interiorShelveProducts: ModelAssociationFragmentEnhanced[] | undefined,
  eventHandler: (params: IAConfigEventHandlerParams) => void
) => {
  const getCurrentProduct = useProductPageStore(state => state.getCurrentProduct);
  const { closeIaConfiguratorWindow } = useIAConfigurator(chainId, chainLength);
  const setIaConfig = useEchainProductStore(state => state.setIaConfig);
  const setSelectedVariantSku = useProductPageStore(state => state.setSelectedVariantSku);
  const session = useSessionStore(state => state.session);
  const { locale } = useRouter();
  const [hasProductVariantSelectionChanged, setHasProductVariantSelectionChanged] = useState(false);

  const updateIaConfigItemPricesAndOrderType = useEchainProductStore(
    store => store.updateIaConfigItemPricesAndOrderType
  );
  const updateCablePrices = useEchainProductStore(store => store.updateCablePrices);
  const ketteNumGlieder = useEchainProductStore(store => store.kette.NumGlieder || 0);

  const priceCountry = getPriceCountry(locale, session);

  const handleEvent = useCallback(
    (event: { data: EicShopSubmitPostMessageDataV1 }) => {
      eventHandler({
        event,
        ketteNumGlieder,
        interiorSeparatorProducts,
        interiorShelveProducts,
        locale,
        priceCountry,
        setIaConfig,
        closeIaConfiguratorWindow,
        updateIaConfigItemPricesAndOrderType,
        updateCablePrices,
        getCurrentProduct,
        setSelectedVariantSku,
        setHasProductVariantSelectionChanged,
      });
    },
    [
      ketteNumGlieder,
      interiorSeparatorProducts,
      interiorShelveProducts,
      locale,
      priceCountry,
      setIaConfig,
      closeIaConfiguratorWindow,
      updateIaConfigItemPricesAndOrderType,
      updateCablePrices,
      getCurrentProduct,
      setSelectedVariantSku,
      setHasProductVariantSelectionChanged,
    ]
  );

  useEffect(() => {
    window.addEventListener('message', handleEvent, false);
    return () => {
      window.removeEventListener('message', handleEvent, false);
    };
  }, [handleEvent]);

  return {
    hasProductVariantSelectionChanged,
    resetHasProductVariantSelectionChanged: () => setHasProductVariantSelectionChanged(false),
  };
};
